/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE-LINE EXERCISE (week editor stage 1, 2026-10-19, `docs/WEEK-EDITOR-STAGE1-PLAN.md`).
 *
 * William writes an exercise in ONE TrueCoach text box; the client's phone needs ROWS (it logs each set, runs the rest and
 * hold timers, draws the charts). So the coach TYPES one line and this reads it into the real fields — and Ioannis's word
 * (10-19): the line reads the shorthand coaches COMMONLY write, for every coach, not one coach's dialect.
 *
 * THE TWO PROMISES, both executed by `exerciseLine.test.ts`:
 *   1. IT NEVER LOSES WORDS. A clause it does not fully understand is kept VERBATIM as the coach's note — so
 *      "rest 2 min between pairs" stays a note and never quietly becomes a 2-minute rest.
 *   2. IT SAYS WHAT IT UNDERSTOOD before anything is saved (`said`), and what it prints back (`showLine`) reads back to
 *      the same fields (`parseLine(showLine(x)) == x`).
 *
 * Clauses split on `,` `;` `·` ` + ` and new lines (a comma between digits is a decimal: `62,5 kg`). A clause whose every
 * token is understood fills fields; a clause that STARTS with a set scheme keeps its scheme and gives its tail to the note
 * (`4x8 @60 slow down`); any other clause is note. Text in double quotes is always note.
 *
 * ⚠️ RPE, RIR AND % OF A MAX HAVE NO COLUMN (`coach_workout_sets` holds reps, kg, time, distance, rounds, rest, type) — they
 * are understood and written into the note in ONE spelling (`RPE 8`), never dropped and never invented as a field.
 * ⚠️ TEMPO IS STORED AS TYPED — never normalised (codex `live-database-discipline`: 220 live tempos, most undashed).
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type LineSetType = "regular" | "warm_up" | "drop_set" | "failure";

/** One set as the line can say it. Rest is the exercise's (one value for every set) — a per-set rest is `lineCanSay` false. */
export type LineSet = {
  type: LineSetType;
  reps: number | null;
  weightKg: number | null;
  timeSeconds: number | null;
  distanceM: number | null;
};

export type LineFields = {
  /** null = the line named no sets: the stored sets are KEPT (a coach who types only "tempo 3-1-1" did not mean "no sets"). */
  sets: LineSet[] | null;
  restSeconds: number | null;
  tempo: string | null;
  eachSide: boolean;
  note: string | null;
};

export type ParsedLine = LineFields & {
  /** What it understood, in reading order — the show-back under the field. */
  said: string[];
};

export type LineUnit = "kg" | "lb";
const KG_PER_LB = 0.45359237;

/* ── clauses ──────────────────────────────────────────────────────────────────────────────────────────────────────── */

type Clause = { text: string; quoted: boolean };

/** Split at `,` `;` `·` ` + ` and new lines, outside double quotes; a comma between digits (`62,5`) is a decimal. */
export function clausesOf(line: string): Clause[] {
  const out: Clause[] = [];
  let cur = "";
  const push = (quoted: boolean) => {
    if (quoted) { out.push({ text: cur, quoted: true }); cur = ""; return; }
    if (cur.trim() !== "") out.push({ text: cur.trim(), quoted: false });
    cur = "";
  };
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === "\"" || ch === "“" || ch === "”") {
      push(false);
      let j = i + 1;
      while (j < line.length && line[j] !== "\"" && line[j] !== "“" && line[j] !== "”") j += 1;
      cur = line.slice(i + 1, j);
      push(true);
      i = j;
      continue;
    }
    if (ch === "," && /\d/.test(line[i - 1] ?? "") && /^\d{1,2}(?![\dx×*])/i.test(line.slice(i + 1))) { cur += "."; continue; }
    if (ch === "," || ch === ";" || ch === "·" || ch === "\n") { push(false); continue; }
    if (ch === "+" && line[i - 1] === " " && line[i + 1] === " ") { push(false); continue; }
    cur += ch;
  }
  push(false);
  return out;
}

/* ── tokens ───────────────────────────────────────────────────────────────────────────────────────────────────────── */

const NUM = String.raw`\d+(?:\.\d+)?`;

/** `90` `90s` `1:30` `2min` `2m` (after `rest`, `m` is minutes) → seconds. */
function seconds(raw: string, bareIs: "s" | "min" = "s"): number | null {
  const t = raw.trim().toLowerCase();
  let m = /^(\d+):(\d{2})$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = /^(\d+(?:\.\d+)?)\s*(s|sec|secs|seconds?|m|min|mins|minutes?|')?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2] ?? bareIs;
  return Math.round(unit === "s" || unit === "sec" || unit === "secs" || unit.startsWith("second") ? n : n * 60);
}

/** `1:30` · `45s` · `1:00:00` — the builder's `clock`, repeated here so this module stays free of the builder. */
export function clockOf(s: number): string {
  if (s < 60) return `${s}s`;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (s >= 3600) return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

function kgOf(n: number, unit: string | undefined, coachUnit: LineUnit): number {
  const u = (unit ?? "").toLowerCase();
  const lb = u.startsWith("lb") || u === "#" || (u === "" && coachUnit === "lb");
  return lb ? Math.round(n * KG_PER_LB * 100) / 100 : n;
}

function fmt(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function distanceWords(m: number): string {
  return m >= 1000 && m % 100 === 0 ? `${fmt(m / 1000)} km` : `${fmt(m)} m`;
}

/** One measure after the `×`: reps (or a range, or `10+`), AMRAP, a time, a distance. */
type Measure =
  | { kind: "reps"; reps: number; top: number | null; plus: boolean }
  | { kind: "amrap" }
  | { kind: "time"; seconds: number }
  | { kind: "distance"; metres: number };

const MEASURE = new RegExp(String.raw`^(?:` +
  String.raw`(?<amrap>amrap|max|failure)` +
  String.raw`|(?<clock>\d+:\d{2})` +
  String.raw`|(?<dist>${NUM})\s*(?<dunit>km|k|metres?|meters?|m)(?![a-z])` +
  String.raw`|(?<time>${NUM})\s*(?<tunit>s|sec|secs|seconds?|min|mins|minutes?)(?![a-z])` +
  String.raw`|(?<reps>\d+)(?:\s*[-–]\s*(?<top>\d+))?(?<plus>\+)?(?:\s*reps?(?![a-z]))?` +
  String.raw`)`, "i");

function measureAt(s: string): { m: Measure; len: number } | null {
  const r = MEASURE.exec(s);
  if (r === null || r.groups === undefined) return null;
  const g = r.groups;
  let m: Measure;
  if (g.amrap !== undefined) m = { kind: "amrap" };
  else if (g.clock !== undefined) m = { kind: "time", seconds: seconds(g.clock) ?? 0 };
  else if (g.dist !== undefined) {
    const u = (g.dunit ?? "").toLowerCase();
    m = { kind: "distance", metres: Number(g.dist) * (u === "km" || u === "k" ? 1000 : 1) };
  } else if (g.time !== undefined) m = { kind: "time", seconds: seconds(`${g.time}${g.tunit ?? ""}`) ?? 0 };
  else m = { kind: "reps", reps: Number(g.reps), top: g.top === undefined ? null : Number(g.top), plus: g.plus !== undefined };
  return { m, len: r[0].length };
}

/* ── one clause ───────────────────────────────────────────────────────────────────────────────────────────────────── */

type Acc = {
  groups: { count: number; measures: Measure[]; loads: (number | null)[]; type: LineSetType | null }[];
  rest: number | null;
  tempo: string | null;
  eachSide: boolean;
  intensities: string[];
  noteBits: string[];
  said: string[];
};

/** Try to read the WHOLE clause. Returns what it understood (applied to `acc` only on success) and whether it also read a
 *  leading set scheme whose tail it could not read (the tail is then note). */
function readClause(text: string, coachUnit: LineUnit, acc: Acc): boolean {
  const work: Acc = { ...acc, groups: [...acc.groups], intensities: [...acc.intensities], said: [...acc.said], noteBits: [...acc.noteBits] };
  let s = text.trim();
  let readAny = false;
  let ledWithScheme = false;
  const eat = (re: RegExp): RegExpExecArray | null => {
    const m = re.exec(s);
    if (m === null) return null;
    s = s.slice(m[0].length).replace(/^\s+/, "");
    readAny = true;
    return m;
  };
  let loop = 0;
  while (s !== "" && loop < 40) {
    loop += 1;
    /* A SET SCHEME: `4x8`, `4 × 8-10`, `3xAMRAP`, `4 sets of 8`, `3 x 0:45`, `5x400m`, or a slash list `5/5/3`. */
    const lead = /^(\d+)\s*(?:x|×|\*|sets?\s+of|sets?\s*x|sets?\s*×)\s*/i.exec(s);
    if (lead !== null) {
      const after = s.slice(lead[0].length);
      const mm = measureAt(after);
      if (mm !== null) {
        if (!readAny) ledWithScheme = true;
        s = after.slice(mm.len).replace(/^\s+/, "");
        readAny = true;
        work.groups.push({ count: Number(lead[1]), measures: [mm.m], loads: [], type: mm.m.kind === "amrap" ? "failure" : null });
        continue;
      }
    }
    const slash = /^(\d+(?:\s*\/\s*\d+)+)(?:\s*reps?(?![a-z]))?/i.exec(s);
    if (slash !== null) {
      if (!readAny) ledWithScheme = true;
      eat(/^(\d+(?:\s*\/\s*\d+)+)(?:\s*reps?(?![a-z]))?/i);
      const reps = slash[1]!.split("/").map((x) => Number(x.trim()));
      work.groups.push({ count: reps.length, measures: reps.map((r) => ({ kind: "reps", reps: r, top: null, plus: false })), loads: [], type: null });
      continue;
    }
    /* `N sets` alone. */
    const nSets = eat(/^(\d+)\s*sets?(?![a-z])/i);
    if (nSets) { if (work.groups.length === 0 && !ledWithScheme) ledWithScheme = true; work.groups.push({ count: Number(nSets[1]), measures: [], loads: [], type: null }); continue; }
    /* AMRAP alone = one set to failure. */
    const amrap = eat(/^(amrap|max reps)(?![a-z])/i);
    if (amrap) {
      const g = work.groups[work.groups.length - 1];
      if (g !== undefined && g.measures.length === 0) { g.type = "failure"; g.measures = [{ kind: "amrap" }]; }
      else work.groups.push({ count: 1, measures: [{ kind: "amrap" }], loads: [], type: "failure" });
      continue;
    }
    /* A TEMPO AFTER `@` (William 10-19, his own line: `3 x 5 reps @ 2010; Rest: 30 secs`): four tempo characters (`2010`,
       `31X0`) or a dashed tempo, with NO unit after them, is the tempo — never a 2010 kg load (it was, until this rule). A
       number with a unit (`@ 1000 kg`) or of another length (`@ 60`, `@ 100`) stays a load. */
    const atTempo = eat(/^@\s*([0-9x]{4}|[0-9x](?:-[0-9x]){2,3})(?![a-z\d%:.\/-])(?!\s*(?:kg|kgs|kilos?|lbs?|rpe|rir|%))/i);
    if (atTempo) { work.tempo = atTempo[1]!.toUpperCase(); continue; }
    /* A LOAD: `@ 60kg`, `@60`, `60 kg`, `135 lb`, `+10kg`, `@ 60/70/80`. A bare number is NOT a load (it is somebody else's:
       `1:30 rest`, `3-1-1`), so the clause is only eaten when it carries `@`, `+` or a unit. */
    const load = new RegExp(String.raw`^(@\s*|\+\s*)?(${NUM}(?:\s*/\s*${NUM})*)\s*(kg|kgs|kilos?|lbs?)?(?![a-z%\d:/-])(?!\s*(?:rpe|rir|%))`, "i").exec(s);
    if (load !== null && (load[1] !== undefined || load[3] !== undefined)) {
      eat(new RegExp(`^.{${String(load[0].length)}}`, "s"));
      const g = work.groups[work.groups.length - 1];
      if (g === undefined) return false;
      g.loads = load[2]!.split("/").map((x) => kgOf(Number(x.trim()), load[3], coachUnit));
      continue;
    }
    let m = eat(/^@?\s*(bw|bodyweight|body weight)(?![a-z])/i);
    if (m) { const g = work.groups[work.groups.length - 1]; if (g === undefined) return false; g.loads = [null]; continue; }
    /* % OF A MAX, RPE, RIR — understood, written into the note in one spelling. */
    m = eat(new RegExp(String.raw`^@?\s*(${NUM})\s*%(?:\s*(?:of\s*)?(?:1rm|max|e1rm))?`, "i"));
    if (m) { work.intensities.push(`${fmt(Number(m[1]))}%`); continue; }
    m = eat(new RegExp(String.raw`^@?\s*rpe\s*(${NUM})|^@?\s*(${NUM})\s*rpe(?![a-z])`, "i"));
    if (m) { work.intensities.push(`RPE ${fmt(Number(m[1] ?? m[2]))}`); continue; }
    m = eat(new RegExp(String.raw`^rir\s*(\d+)|^(\d+)\s*rir(?![a-z])`, "i"));
    if (m) { work.intensities.push(`RIR ${m[1] ?? m[2]}`); continue; }
    /* SET KINDS after a group. */
    m = eat(/^(warm[\s-]?ups?|wu|w\/u)(?![a-z])/i);
    if (m) { const g = work.groups[work.groups.length - 1]; if (g === undefined) return false; g.type = "warm_up"; continue; }
    m = eat(/^(drop\s*sets?|drops?)(?![a-z])/i);
    if (m) { const g = work.groups[work.groups.length - 1]; if (g === undefined) return false; g.type = "drop_set"; continue; }
    m = eat(/^(to\s+failure|failure)(?![a-z])/i);
    if (m) { const g = work.groups[work.groups.length - 1]; if (g === undefined) return false; g.type = "failure"; continue; }
    /* REST: `rest 90`, `rest: 1:30`, `rest 2min`, `90s rest`. After the word, a bare number is seconds, `2m` minutes. */
    m = eat(/^rest\s*:?\s*(\d+:\d{2}|\d+(?:\.\d+)?\s*(?:s|sec|secs|seconds?|m|min|mins|minutes?|')?)(?![a-z\d:.])/i);
    /* ⚠️ `rest 3` is three MINUTES: a coach never prescribes a 3-second rest, and a bare 1–9 after the word is how a rest in
       minutes is written. 10 and up are seconds (`rest 90`). */
    if (m) {
      const raw = m[1]!.trim();
      const v = /^\d$/.test(raw) ? Number(raw) * 60 : seconds(raw.replace(/m$/i, "min"));
      if (v === null) return false;
      work.rest = v;
      continue;
    }
    m = eat(/^(\d+:\d{2}|\d+(?:\.\d+)?\s*(?:s|sec|secs|seconds?|min|mins|minutes?))\s*rest(?![a-z])/i);
    if (m) { const v = seconds(m[1]!); if (v === null) return false; work.rest = v; continue; }
    /* TEMPO: with the word, dashed or not; without it, only dashed with three or four parts (`3-1-1`, `2-0-X-0`). */
    m = eat(/^(?:tempo|t)\s*:?\s*([0-9x]{1,2}(?:\s*[-/]\s*[0-9x]{1,2}){2,3}|[0-9x]{3,4})(?![a-z\d])/i);
    if (m) { work.tempo = m[1]!.replace(/\s+/g, "").toUpperCase(); continue; }
    m = eat(/^([0-9x](?:-[0-9x]){2,3})(?![a-z\d])/i);
    if (m) { work.tempo = m[1]!.toUpperCase(); continue; }
    /* EACH SIDE. */
    m = eat(/^(each\s+(?:side|leg|arm)|per\s+(?:side|leg|arm)|\/\s*(?:side|leg|arm)|e\/s|each|a\s+side)(?![a-z])/i);
    if (m) { work.eachSide = true; continue; }
    /* `reps` on its own after a group's count (`4 x 8 reps` is already eaten by the measure). */
    m = eat(/^reps?(?![a-z])/i);
    if (m) continue;
    break;
  }
  if (s === "") { Object.assign(acc, work); return true; }
  /* A clause that LED with a set scheme keeps it; its tail is the coach's words. */
  if (ledWithScheme && work.groups.length > acc.groups.length) {
    const tail = s.trim();
    Object.assign(acc, work);
    acc.noteBits.push(tail);
    return true;
  }
  return false;
}

/* ── the line ─────────────────────────────────────────────────────────────────────────────────────────────────────── */

function setsOf(groups: Acc["groups"]): { sets: LineSet[]; rangeNotes: string[] } {
  const sets: LineSet[] = [];
  const rangeNotes: string[] = [];
  for (const g of groups) {
    for (let i = 0; i < g.count; i += 1) {
      const m = g.measures.length === g.count ? g.measures[i] : g.measures[0];
      const load = g.loads.length === g.count ? g.loads[i] : g.loads.length === 1 ? g.loads[0] : g.loads[Math.min(i, g.loads.length - 1)];
      const set: LineSet = { type: g.type ?? "regular", reps: null, weightKg: load ?? null, timeSeconds: null, distanceM: null };
      if (m?.kind === "reps") { set.reps = m.reps; if (m.plus) set.type = "failure"; }
      if (m?.kind === "amrap") set.type = "failure";
      if (m?.kind === "time") set.timeSeconds = m.seconds;
      if (m?.kind === "distance") set.distanceM = m.metres;
      sets.push(set);
    }
    const m0 = g.measures[0];
    if (m0?.kind === "reps" && m0.top !== null) rangeNotes.push(`${m0.reps}–${m0.top} reps`);
  }
  return { sets, rangeNotes };
}

/** Read a typed line. `coachUnit` decides what a bare `@ 60` means; `lb` is always stored as kg. */
export function parseLine(line: string, coachUnit: LineUnit = "kg"): ParsedLine {
  const acc: Acc = { groups: [], rest: null, tempo: null, eachSide: false, intensities: [], noteBits: [], said: [] };
  for (const c of clausesOf(line)) {
    if (c.quoted) { if (c.text.trim() !== "") acc.noteBits.push(c.text); continue; }
    if (!readClause(c.text, coachUnit, acc)) acc.noteBits.push(c.text);
  }
  const { sets, rangeNotes } = setsOf(acc.groups);
  const noteParts = [...acc.intensities, ...rangeNotes, ...acc.noteBits].map((x) => x.trim()).filter((x) => x !== "");
  const note = noteParts.length === 0 ? null : noteParts.join(", ");
  const fields: LineFields = {
    sets: acc.groups.length === 0 ? null : sets,
    restSeconds: acc.rest,
    tempo: acc.tempo,
    eachSide: acc.eachSide,
    note,
  };
  return { ...fields, said: saidOf(fields) };
}

/** The show-back: what the line will write, in the coach's words. */
export function saidOf(f: LineFields): string[] {
  const out: string[] = [];
  if (f.sets === null) out.push("sets unchanged");
  else if (f.sets.length === 0) out.push("no sets");
  else out.push(...groupWords(f.sets));
  if (f.tempo !== null) out.push(`tempo ${f.tempo}`);
  if (f.restSeconds !== null) out.push(`rest ${clockOf(f.restSeconds)}`);
  if (f.eachSide) out.push("each side");
  if (f.note !== null) out.push(`note “${f.note}”`);
  return out;
}

function sameSet(a: LineSet, b: LineSet): boolean {
  return a.type === b.type && a.reps === b.reps && a.weightKg === b.weightKg && a.timeSeconds === b.timeSeconds && a.distanceM === b.distanceM;
}

/** Runs of identical sets, in order. */
function runs(sets: readonly LineSet[]): { set: LineSet; count: number }[] {
  const out: { set: LineSet; count: number }[] = [];
  for (const s of sets) {
    const last = out[out.length - 1];
    if (last !== undefined && sameSet(last.set, s)) last.count += 1;
    else out.push({ set: s, count: 1 });
  }
  return out;
}

/** `4 × 8 @ 60 kg` per run; the show-back says `sets` in words, the line says the shorthand. */
function groupText(set: LineSet, count: number, unit: LineUnit): string {
  let measure = "";
  if (set.distanceM !== null) measure = distanceWords(set.distanceM);
  else if (set.timeSeconds !== null) measure = clockOf(set.timeSeconds);
  else if (set.reps !== null) measure = `${set.reps}${set.type === "failure" ? "+" : ""}`;
  else if (set.type === "failure") measure = "AMRAP";
  let t = measure === "" ? `${count} ${count === 1 ? "set" : "sets"}` : `${count} × ${measure}`;
  if (set.weightKg !== null) t += unit === "lb" ? ` @ ${fmt(Math.round((set.weightKg / KG_PER_LB) * 2) / 2)} lb` : ` @ ${fmt(set.weightKg)} kg`;
  if (set.type === "warm_up") t += " warm-up";
  if (set.type === "drop_set") t += " drop";
  if (set.type === "failure" && set.reps === null && measure !== "AMRAP") t += " to failure";
  return t;
}

function groupWords(sets: readonly LineSet[]): string[] {
  return runs(sets).map(({ set, count }) => groupText(set, count, "kg"));
}

/** Can the line say this exercise without losing anything? Per-set rests and rounds cannot be said; `Sets…` opens the table. */
export function lineCanSay(sets: readonly { restSeconds: number | null; rounds: number | null; timeSeconds: number | null; distanceM: number | null }[]): boolean {
  if (sets.some((s) => s.rounds !== null)) return false;
  if (sets.some((s) => s.timeSeconds !== null && s.distanceM !== null)) return false;
  const rests = new Set(sets.map((s) => s.restSeconds));
  return rests.size <= 1;
}

/** The line as the field shows it: `4 × 8 @ 60 kg, tempo 3-1-1, rest 1:30, each side, "the note"`. */
export function showLine(f: LineFields, unit: LineUnit = "kg"): string {
  const parts: string[] = [];
  if (f.sets !== null) parts.push(...runs(f.sets).map(({ set, count }) => groupText(set, count, unit)));
  if (f.tempo !== null) parts.push(`tempo ${f.tempo}`);
  if (f.restSeconds !== null) parts.push(`rest ${clockOf(f.restSeconds)}`);
  if (f.eachSide) parts.push("each side");
  if (f.note !== null && f.note.trim() !== "") parts.push(`"${f.note.replace(/["“”]/g, "'")}"`);
  return parts.join(", ");
}
